import {
  ChangeDetectionStrategy, Component, DestroyRef, inject, input, OnChanges, OnInit, output,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import {
  FormArray, FormBuilder, FormGroup, ReactiveFormsModule,
} from '@angular/forms';
import { TranslateModule } from '@ngx-translate/core';
import {
  InputType, TnButtonComponent, TnCardComponent, TnCardHeaderDirective, TnFormFieldComponent,
  TnIconButtonComponent, TnInputComponent, TnSelectComponent, TnSelectOption,
} from '@truenas/ui-components';
import { debounceTime } from 'rxjs/operators';
import { JobState } from 'app/enums/job-state.enum';
import { MockEvent } from 'app/modules/websocket-debug-panel/interfaces/mock-config.interface';
import { parseDelay, safeJsonParse, safeJsonStringify } from 'app/modules/websocket-debug-panel/utils/type-guards';

@Component({
  selector: 'ix-job-event-builder',
  standalone: true,
  imports: [
    ReactiveFormsModule,
    TnCardComponent,
    TnCardHeaderDirective,
    TnButtonComponent,
    TranslateModule,
    TnIconButtonComponent,
    TnFormFieldComponent,
    TnInputComponent,
    TnSelectComponent,
  ],
  templateUrl: './job-event-builder.component.html',
  styleUrls: ['./job-event-builder.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class JobEventBuilderComponent implements OnInit, OnChanges {
  readonly events = input<MockEvent[]>([]);
  readonly eventsChange = output<MockEvent[]>();

  private readonly fb = inject(FormBuilder);

  private destroyRef = inject(DestroyRef);
  private isUpdatingFromInput = false;

  protected readonly InputType = InputType;
  protected readonly JobState = JobState;
  protected readonly stateOptions: TnSelectOption<JobState>[] = [
    { label: 'RUNNING', value: JobState.Running },
    { label: 'SUCCESS', value: JobState.Success },
    { label: 'FAILED', value: JobState.Failed },
    { label: 'ABORTED', value: JobState.Aborted },
    { label: 'WAITING', value: JobState.Waiting },
  ];

  protected form = this.fb.group({
    events: this.fb.array<FormGroup>([]),
  });

  constructor() {
    // Emit changes when form updates with debounce to prevent partial values
    this.form.valueChanges.pipe(
      debounceTime(300),
      takeUntilDestroyed(this.destroyRef),
    ).subscribe(() => {
      if (!this.isUpdatingFromInput) {
        const formEvents = this.getFormEvents();
        this.eventsChange.emit(formEvents);
      }
    });
  }

  ngOnChanges(): void {
    // Only update if the events actually changed to prevent feedback loops
    const newEvents = this.events();
    const currentEvents = this.getFormEvents();

    if (!this.areEventsEqual(newEvents, currentEvents)) {
      this.setFormEvents(newEvents);
    }
  }

  ngOnInit(): void {
    this.setFormEvents(this.events());
  }

  get eventsFormArray(): FormArray {
    return this.form.controls.events;
  }

  private createEventFormGroup(event: MockEvent): FormGroup {
    const delayValue = parseDelay(event.delay);

    return this.fb.group({
      delay: [delayValue],
      state: [event.fields.state],
      description: [event.fields.description || ''],
      progressPercent: [event.fields.progress?.percent !== undefined && event.fields.progress?.percent !== null
        ? event.fields.progress.percent
        : 0],
      progressDescription: [event.fields.progress?.description || ''],
      result: [safeJsonStringify(event.fields.result)],
      error: [event.fields.error || ''],
    });
  }

  private setFormEvents(events: MockEvent[]): void {
    this.isUpdatingFromInput = true;
    this.eventsFormArray.clear();
    events.forEach((event) => {
      this.eventsFormArray.push(this.createEventFormGroup(event));
    });
    this.isUpdatingFromInput = false;
  }

  private getFormEvents(): MockEvent[] {
    return this.eventsFormArray.controls.map((control) => {
      const formGroup = control as FormGroup;
      const value = formGroup.value as {
        delay: number;
        state: JobState;
        description: string;
        progressPercent: number;
        progressDescription: string;
        result: string;
        error: string;
      };
      const event: MockEvent = {
        delay: parseDelay(value.delay),
        fields: {
          state: value.state,
          description: value.description || null,
          progress: {
            percent: value.progressPercent !== undefined && value.progressPercent !== null
              ? Number(value.progressPercent)
              : 0,
            description: value.progressDescription || '',
          },
        },
      };

      if (value.result) {
        event.fields.result = safeJsonParse(value.result, value.result);
      }

      if (value.error) {
        event.fields.error = value.error;
      }

      return event;
    });
  }

  protected addEvent(): void {
    const newEvent: MockEvent = {
      delay: 2000,
      fields: {
        description: 'Processing...',
        progress: {
          percent: 0,
          description: 'Starting...',
        },
        state: JobState.Running,
      },
    };
    this.eventsFormArray.push(this.createEventFormGroup(newEvent));
  }

  protected removeEvent(index: number): void {
    this.eventsFormArray.removeAt(index);
  }

  protected getEventControl(index: number): FormGroup {
    const control = this.eventsFormArray.at(index);
    if (!control) {
      throw new Error(`Event control at index ${index} not found`);
    }
    return control as FormGroup;
  }

  protected isResultVisible(index: number): boolean {
    const control = this.getEventControl(index);
    const state = control.controls.state?.value as JobState;
    return state === JobState.Success || state === JobState.Failed;
  }

  private areEventsEqual(events1: MockEvent[], events2: MockEvent[]): boolean {
    if (events1.length !== events2.length) {
      return false;
    }

    return events1.every((event1, index) => {
      const event2 = events2[index];

      // Compare basic properties
      if (event1.delay !== event2.delay) {
        return false;
      }

      // Compare fields
      const fields1 = event1.fields;
      const fields2 = event2.fields;

      if (fields1.state !== fields2.state
        || fields1.description !== fields2.description
        || fields1.error !== fields2.error) {
        return false;
      }

      // Compare progress
      if (fields1.progress?.percent !== fields2.progress?.percent
        || fields1.progress?.description !== fields2.progress?.description) {
        return false;
      }

      // Compare result (both could be undefined/null or objects)
      if (fields1.result !== fields2.result) {
        // If they're not strictly equal, check if they're both objects
        if (typeof fields1.result === 'object' && typeof fields2.result === 'object') {
          // Simple object comparison - could be enhanced for nested objects
          return JSON.stringify(fields1.result) === JSON.stringify(fields2.result);
        }
        return false;
      }

      return true;
    });
  }
}
